import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import PageMeta from '@/components/feature/PageMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { novels, categories } from '@/mocks/novels';
import type { Novel } from '@/mocks/novels';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 9;

  const currentCategory = categories.find((c) => c.slug === slug);

  const filteredNovels = useMemo(() => {
    if (!slug) return [];
    let result = novels.filter((n) => n.categorySlug === slug);
    if (sortBy === 'popular') {
      result = [...result].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else {
      result = [...result].sort(
        (a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
      );
    }
    return result;
  }, [slug, sortBy]);

  const totalPages = Math.ceil(filteredNovels.length / pageSize);
  const pagedNovels = filteredNovels.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const categorySchema = currentCategory ? {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 嫂子的诱惑`,
    description: currentCategory.description,
    url: `https://cbdti.cn/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '嫂子的诱惑',
      url: 'https://cbdti.cn/',
    },
  } : null;

  if (!currentCategory) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <p className="text-foreground-500 text-lg mb-4">该分类不存在</p>
          <Link to="/" className="text-primary-600 hover:text-primary-700 transition-colors">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <PageMeta
      title={`${currentCategory.name} - 嫂子的诱惑 | 都市情感小说分类推荐`}
      description={currentCategory.description}
      keywords={`${currentCategory.name},都市情感小说,言情网络小说,禁忌爱情故事,热门都市小说`}
      canonicalUrl={`https://cbdti.cn/category/${slug}`}
      ogTitle={`${currentCategory.name} - 嫂子的诱惑`}
      ogDescription={currentCategory.description}
      ogType="website"
      ogUrl={`https://cbdti.cn/category/${slug}`}
      twitterCard="summary_large_image"
      jsonLd={categorySchema || undefined}
    >
      <main className="min-h-screen bg-background-50">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-14">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory.name },
            ]}
          />

          <div className="mt-6 mb-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className={`${currentCategory.icon} text-lg`}></i>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">
                {currentCategory.name}
              </h1>
            </div>
            <p className="text-sm text-foreground-600 ml-13">{currentCategory.description}</p>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <p className="text-sm text-foreground-500">
              共找到 <strong className="text-foreground-800">{filteredNovels.length}</strong> 部作品
            </p>
            <div className="flex items-center gap-1 bg-background-100 border border-background-200/70 rounded-lg p-1">
              {[
                { key: 'latest', label: '最新发布' },
                { key: 'popular', label: '最多阅读' },
                { key: 'rating', label: '最高评分' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => { setSortBy(opt.key as typeof sortBy); setCurrentPage(1); }}
                  className={`px-4 py-1.5 text-sm rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {pagedNovels.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {pagedNovels.map((novel: Novel) => (
                  <Link
                    key={novel.id}
                    to={`/article/${novel.id}`}
                    className="group bg-background-100 rounded-lg border border-background-200/70 overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={novel.coverImage}
                        alt={novel.title + ' - ' + currentCategory.name + '小说推荐'}
                        title={novel.title + ' - 都市情感小说'}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full whitespace-nowrap">
                          {novel.category}
                        </span>
                        {novel.isHot && (
                          <span className="text-xs px-2 py-0.5 bg-accent-100 text-accent-800 rounded-full whitespace-nowrap">
                            热门
                          </span>
                        )}
                      </div>
                      <h3 className="font-heading text-base text-foreground-900 mb-1 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {novel.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-3">
                        {novel.description}
                      </p>
                      <div className="flex items-center justify-between pt-3 border-t border-background-200/70">
                        <span className="text-xs text-foreground-400">{novel.author}</span>
                        <span className="flex items-center gap-1 text-xs text-accent-600">
                          <i className="ri-star-fill text-xs"></i>
                          {novel.rating}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 text-sm rounded-lg border border-background-200/70 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    上一页
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`w-9 h-9 text-sm rounded-lg transition-all duration-200 cursor-pointer ${
                        currentPage === p
                          ? 'bg-primary-500 text-background-50 font-medium'
                          : 'text-foreground-600 hover:bg-background-100 border border-background-200/70'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 text-sm rounded-lg border border-background-200/70 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    下一页
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500">该分类下暂无作品</p>
            </div>
          )}
        </div>
      </main>
    </PageMeta>
  );
}